import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, IconButton, Modal, Typography } from '@mui/material';

import WaeButton, { BUTTON_TYPE, BUTTON_VARIANT } from '../../../../../../../components/Button';
import StatusBadge from '../../../../../../../components/EmployeeDocStatusBadge';
import { BLACK, PRIMARY_PURPLE } from '../../../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../../../theme/fontConstants';
import {
  epochToDateInReadableFormat,
  epochToTimeInReadableFormat,
} from '../../../../../../../utils';
import { getStatusUi } from '../documentDisplay';

import DocumentHistoryRowMenu from './DocumentHistoryRowMenu';

const modalRootSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '600px' },
  maxHeight: '80vh',
  bgcolor: BLACK[0],
  borderRadius: '16px',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

const headerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleSx = {
  fontSize: '20px',
  fontFamily: PRIMARY_FONT[700],
  color: BLACK[100],
  lineHeight: '24px',
};

const closeIconButtonSx = {
  color: BLACK[60],
  p: 0.5,
};

const listContainerSx = {
  flex: 1,
  overflow: 'auto',
  maxHeight: '50vh',
};

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  py: 1.5,
  borderBottom: `1px solid ${BLACK[20]}`,
};

const rowNameSx = {
  fontFamily: PRIMARY_FONT[500],
  fontSize: '15px',
  color: BLACK[100],
  mb: 0.5,
};

const rowRightSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 0.5,
};

const uploadDateSx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '12px',
  color: BLACK[60],
};

const viewLinkSx = {
  minWidth: 0,
  height: 'auto',
  px: 0,
  py: 0,
  boxShadow: 'none',
  textTransform: 'none',
  fontFamily: PRIMARY_FONT[500],
  fontSize: '14px',
  color: PRIMARY_PURPLE[70],
  '&:hover': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    color: PRIMARY_PURPLE[60],
  },
  '&:disabled': {
    color: BLACK[40],
    cursor: 'default',
  },
};

const emptyStateSx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '14px',
  color: BLACK[60],
  fontStyle: 'italic',
  textAlign: 'center',
  py: 3,
};

const loadingStateSx = {
  display: 'flex',
  justifyContent: 'center',
  py: 3,
};

const errorStateSx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '14px',
  color: BLACK[60],
  textAlign: 'center',
  py: 3,
};

const historyListSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
};

const footerSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  borderTop: `1px solid ${BLACK[20]}`,
  pt: 2,
};

const closeButtonSx = {
  borderRadius: '100px',
  color: BLACK[0],
  fontFamily: PRIMARY_FONT[500],
  fontSize: '14px',
  px: 3,
  py: 1,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: PRIMARY_PURPLE[60],
    boxShadow: 'none',
  },
};

const DocumentUploadHistoryModal = ({
  open,
  onClose,
  documents,
  onView,
  onRequestDelete,
  onRequestFormStatus,
  onRequestFormValidity,
  onRequestSetExpiration,
  activeActionKey,
  isLoading,
  hasError,
  actionErrorKey,
  disableEnforceFocus,
}) => {
  const renderBodyContent = () => {
    if (isLoading) {
      return (
        <Box sx={loadingStateSx}>
          <CircularProgress size={24} sx={{ color: PRIMARY_PURPLE[60] }} />
        </Box>
      );
    }

    if (hasError) {
      return (
        <Typography sx={errorStateSx}>
          {LanguageConverterV2('userDetail.employeeDocumentation.history.error')}
        </Typography>
      );
    }

    if (!documents?.length) {
      return (
        <Typography sx={emptyStateSx}>
          {LanguageConverterV2('userDetail.employeeDocumentation.history.empty')}
        </Typography>
      );
    }

    return (
      <Box sx={historyListSx}>
        {documents.map((document) => {
          const statusUi = getStatusUi(document?.state?.status);
          const formattedDate = epochToDateInReadableFormat(document?.metadata?.modifiedAt);
          const formattedTime = epochToTimeInReadableFormat(document?.metadata?.modifiedAt);
          const isActionLoading = activeActionKey === `view:${document._id}`;

          return (
            <Box key={document._id} sx={rowSx}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={rowNameSx}>
                  {document?.file?.filename || document?.documentType}
                </Typography>
                <StatusBadge
                  label={LanguageConverterV2(statusUi.statusLabelKey)}
                  variant={statusUi.statusVariant}
                />
              </Box>
              <Box sx={rowRightSx}>
                <Typography sx={uploadDateSx}>
                  {LanguageConverterV2('userDetail.employeeDocumentation.history.uploadDate')}
                  {': '}
                  {formattedDate}
                  {formattedDate && formattedTime ? ', ' : ''}
                  {formattedTime}
                </Typography>
                <WaeButton
                  type={BUTTON_TYPE.BUTTON}
                  variant={BUTTON_VARIANT.DEFAULT}
                  actionColor="transparent"
                  hoverColor="transparent"
                  disabled={isActionLoading}
                  onClick={() => onView(document._id)}
                  sx={viewLinkSx}
                >
                  {LanguageConverterV2('userDetail.employeeDocumentation.action.view')}
                </WaeButton>
              </Box>
              <DocumentHistoryRowMenu
                document={document}
                onRequestDelete={onRequestDelete}
                onSelectFormStatus={onRequestFormStatus}
                onSelectFormValidity={onRequestFormValidity}
                onSelectSetExpiration={onRequestSetExpiration}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableEnforceFocus={disableEnforceFocus}
      aria-labelledby="document-upload-history-modal-title"
      aria-describedby="document-upload-history-modal-description"
    >
      <Box sx={modalRootSx}>
        <Box sx={headerSx}>
          <Typography id="document-upload-history-modal-title" sx={titleSx}>
            {LanguageConverterV2('userDetail.employeeDocumentation.history.title')}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={closeIconButtonSx}
            aria-label={LanguageConverterV2('button.close')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {actionErrorKey && (
          <Typography sx={errorStateSx}>{LanguageConverterV2(actionErrorKey)}</Typography>
        )}

        <Box id="document-upload-history-modal-description" sx={listContainerSx}>
          {renderBodyContent()}
        </Box>

        <Box sx={footerSx}>
          <WaeButton
            type={BUTTON_TYPE.BUTTON}
            variant={BUTTON_VARIANT.DEFAULT}
            actionColor={PRIMARY_PURPLE[70]}
            hoverColor={PRIMARY_PURPLE[60]}
            onClick={onClose}
            sx={closeButtonSx}
          >
            {LanguageConverterV2('button.close')}
          </WaeButton>
        </Box>
      </Box>
    </Modal>
  );
};

DocumentUploadHistoryModal.propTypes = {
  actionErrorKey: PropTypes.string,
  activeActionKey: PropTypes.string,
  disableEnforceFocus: PropTypes.bool,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      documentType: PropTypes.string,
      file: PropTypes.shape({ filename: PropTypes.string }),
      metadata: PropTypes.shape({ modifiedAt: PropTypes.number }),
      state: PropTypes.shape({ status: PropTypes.string }),
      validity: PropTypes.shape({
        status: PropTypes.string,
        range: PropTypes.shape({
          validFrom: PropTypes.string,
          validNoticeFrom: PropTypes.string,
          validThrough: PropTypes.string,
        }),
      }),
    })
  ),
  hasError: PropTypes.bool,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onRequestDelete: PropTypes.func,
  onRequestFormStatus: PropTypes.func,
  onRequestFormValidity: PropTypes.func,
  onRequestSetExpiration: PropTypes.func,
  onView: PropTypes.func,
  open: PropTypes.bool.isRequired,
};

DocumentUploadHistoryModal.defaultProps = {
  actionErrorKey: null,
  activeActionKey: null,
  disableEnforceFocus: false,
  documents: [],
  hasError: false,
  isLoading: false,
  onRequestDelete: () => {},
  onRequestFormStatus: () => {},
  onRequestFormValidity: () => {},
  onRequestSetExpiration: () => {},
  onView: () => {},
};

export default DocumentUploadHistoryModal;
